/**
 * API e stato server della lettura quotidiana delle notizie (F-185).
 *
 * ⚠️ Gli importi sono STRINGHE decimali (ADR-0006).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { AssetDomain } from '@/lib/asset-domain';

export type NewsReviewSettings = {
  enabled: boolean;
  runAtLocalTime: string;
  timezone: string;
};

export type NewsReviewRunStatus = 'queued' | 'running' | 'completed' | 'failed';

export type NewsReviewProposal = {
  id: string;
  code: string | null;
  side: 'buy' | 'sell';
  /** Lo stato vivo della proposta nel motore. */
  status: string;
  instrumentId: string;
  instrumentName: string;
  symbol: string | null;
  portfolioName: string;
  amount: string | null;
  currency: string;
  rejectionDetail: string | null;
};

export type NewsReviewPick = {
  status: 'added' | 'skipped' | 'discarded';
  instrumentId: string;
  name: string;
  symbol: string | null;
  domain: AssetDomain | null;
  confidence: number;
  reason: string;
  note: string | null;
  wishlistItemId: string | null;
  portfolioName: string | null;
  expiresAt: string | null;
};

export type NewsReviewRun = {
  id: string;
  trigger: 'scheduled' | 'manual';
  status: NewsReviewRunStatus;
  slotDate: string | null;
  agentRunId: string | null;
  newsCount: number;
  candidatesCount: number;
  proposalsCount: number;
  proposals: NewsReviewProposal[];
  picks: NewsReviewPick[];
  relevantNews: Array<{ newsItemId: string; title: string; note: string; url: string | null }>;
  summary: string | null;
  emptyReason: string | null;
  errorMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
};

export type NewsReviewOverview = {
  settings: NewsReviewSettings;
  nextRunAt: string | null;
  agentRuntimeEnabled: boolean;
  windowHours: number;
  maxProposals: number;
  maxWishlistPicks: number;
  defaultExpiryDays: number;
  today: {
    newsCount: number;
    operationsDisabledReason: string | null;
    operablePortfolios: Array<{ id: string; name: string; mode: string }>;
    buyableCount: number;
    sellableCount: number;
    targets: Record<AssetDomain, { portfolioId: string; portfolioName: string } | null>;
  };
  runs: NewsReviewRun[];
};

export const newsReviewKeys = { overview: ['news-review'] as const };

export function isInFlight(run: { status?: NewsReviewRunStatus | undefined } | undefined): boolean {
  return run?.status === 'queued' || run?.status === 'running';
}

export function useNewsReviewOverview() {
  return useQuery({
    queryKey: newsReviewKeys.overview,
    queryFn: ({ signal }) => api.get<NewsReviewOverview>('/news-review', signal),
    // Mentre uno studio è in coda o in corso la card si aggiorna da sola: dura minuti.
    refetchInterval: (query) => (isInFlight(query.state.data?.runs[0]) ? 10_000 : false),
  });
}

export function useUpdateNewsReviewSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<NewsReviewSettings>) => api.put<NewsReviewOverview>('/news-review/settings', input),
    onSuccess: (overview) => queryClient.setQueryData(newsReviewKeys.overview, overview),
  });
}

export function useRunNewsReviewNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ runId: string }>('/news-review/runs'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: newsReviewKeys.overview });
    },
  });
}
